import { AxiosResponse } from 'axios';
import { getTokenAsQuery } from '@/features/auth/helpers';
import {
  AmountVerificationResponse,
  AvailableService,
  CarrierLabelResult,
  CarrierServiceLevel,
  CityProvinceResidentialResponse,
  GetLabelData,
  GetRatesData,
  InProgress,
  IntegrationsResponse,
  PaymentInfoRequest,
  PaymentInfoResponse,
  PaymentSavedResponse,
  PrintLabelSize,
  ReconnectAccounts,
  ShipmentPrintData,
  ShippingTips,
  StoreForImport,
  PredictionAddressDto,
  ClarificationAddressDto,
  ThreeDsCardLookupDto,
  ThreeDsChallengeDto,
  CountryMetadataResponse,
  PayDetailsAddressDto,
  LandedCostResponse,
} from '@/features/shipment/types';
import { useAxios } from '@/features/common/helpers/axios';
import i18n from '@/i18n';
import { parseError, filterExceptionMessage } from '@/features/common/helpers/errorParser';
import { GlobalResponseType } from '@/features/common/types';
import { parseValidInProgressData } from '../helpers/createShipment';

export const { axiosService, baseURL } = useAxios();

export const { axiosService: axiosServiceV2, baseURL: baseURLV2 } = useAxios(2);

export const { axiosService: axiosServiceV3, baseURL: baseURLV3 } = useAxios(3);

export const acknowledgeShipForSuccess = async (): Promise<boolean> => {
  try {
    await axiosService.post(
      '/account/user-details/ship-for-success/acknowledged', {},
    );
    return true;
  } catch (err) {
    return false;
  }
};

export const parseCarriersData = async (
  carriersData: GetRatesData,
  caller: 'Rates' | 'CreateShipment',
): Promise<{
  rates: AvailableService[],
  reconnectAccounts: ReconnectAccounts,
} | null> => {
  try {
    const carriersResponse: AxiosResponse = await axiosServiceV3.post(
      `/carriers/rates?caller=${caller}`,
      // '/carriers/mockrates',
      carriersData,
    );
    return carriersResponse.data;
  } catch (err) {
    return null;
  }
};

export const getLandedCost = async (
  inProgress: InProgress,
  service: AvailableService,
): Promise<LandedCostResponse | null> => {
  try {
    const { commercialInvoice, toAddress, fromAddress } = inProgress;
    const customsParts = commercialInvoice?.customsParts || [];

    if (!customsParts.length) return null;

    const shipmentItems = customsParts.map((part, index) => ({
      commodityId: index + 1,
      priceEach: part.unitValue,
      quantity: part.quantity,
      UOM: part.quantityUnits || 'Each',
      originCountryCode: part.countryOfOrigin,
      commodityCurrencyCode: commercialInvoice.currencyCode || 'CAD',
      hsCode: part.hsCode,
      description: part.detailedDescription,
    }));

    const requestBody = {
      currencyCode: commercialInvoice.currencyCode || 'CAD',
      allowPartialLandedCostResult: false,
      shipment: {
        id: 1,
        importCountryCode: toAddress.countryCode,
        exportCountryCode: fromAddress.countryCode,
        importPostalCode: toAddress.postalCode,
        importProvince: toAddress.province,
        shipmentType: commercialInvoice.reasonForExport || 'Permanent',
        incoterms: 'DAP',
        serviceLevel: service.selectedService,
        transportCost: service.total,
        shipmentItems,
      },
    };

    const response: AxiosResponse = await axiosService.post('/landed-cost', requestBody);
    return response.data;
  } catch (err) {
    return null;
  }
};

export const getInProgressData = async (): Promise<InProgress | null> => {
  try {
    const response: AxiosResponse = await axiosService.get('/shipment/in-progress');
    if (!response.data) return null;
    return response.data;
  } catch (err) {
    return null;
  }
};

export const clearAllInformation = async (): Promise<boolean> => {
  try {
    const response: AxiosResponse = await axiosService.delete('/shipment/in-progress');
    return response.data;
  } catch (err) {
    return false;
  }
};

export const syncInProgressData = async (
  inProgressData: InProgress,
): Promise<boolean> => {
  try {
    await axiosService.post(
      '/shipment/in-progress',
      parseValidInProgressData(inProgressData),
    );
    return true;
  } catch (err) {
    return false;
  }
};

// TODO: move this to e-commerce module
export const getIntegrationsConnectedData = async (): Promise<StoreForImport[] | null> => {
  try {
    const response: AxiosResponse = await axiosService.get('/integrations/connected');
    return response.data;
  } catch (err) {
    return null;
  }
};

export const createLabelForCarriers = async (
  data: GetLabelData,
): Promise<CarrierLabelResult> => {
  // Strip taxId for domestic shipments to prevent carrier validation errors (e.g. Purolator)
  const isDomestic = data.fromAddress.countryCode === data.toAddress.countryCode;
  const payload: GetLabelData = isDomestic
    ? {
      ...data,
      fromAddress: { ...data.fromAddress, taxId: undefined },
      toAddress: { ...data.toAddress, taxId: undefined },
    }
    : data;

  let isWarning = false;

  try {
    const response: AxiosResponse = await axiosService.post(
      '/carriers/create-label',
      payload,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      secureshipTrackingNo: response.data.trackingNumber,
      carrierTrackingNos: response.data.carrierTrackingNos,
      isWarning,
      isCarrierMessage: false,
    };
  } catch (err) {
    const parsedError = parseError(err);

    let redirectUrl = '';
    let isCarrierMessage = false;
    const { errorType } = parsedError;
    let { errorTitle, errorBanner, errorMessage } = parsedError;
    const isExternalLink = false;
    let threeDsChallenge: ThreeDsChallengeDto | undefined;

    if (errorType === 'CreditCardChallengeRequired' && err.response?.data?.detail) {
      try {
        const errDetail = JSON.parse(err.response.data.detail);
        if (errDetail.threeDSecureChallengeUrl
          && errDetail.threeDSecureChallengeData
          && errDetail.threeDSecureAuthenticationId
          && errDetail.threeDSecureVersion) {
          threeDsChallenge = {
            threeDSecureChallengeUrl: errDetail.threeDSecureChallengeUrl,
            threeDSecureChallengeData: errDetail.threeDSecureChallengeData,
            threeDSecureAuthenticationId: errDetail.threeDSecureAuthenticationId,
            threeDSecureVersion: errDetail.threeDSecureVersion,
          };
        }
      } catch (parseErr) {
        // If parsing fails, continue without challenge data
      }
    }

    switch (errorType) {
      case undefined:
      case null:
        break;
      case 'ThreeDsCreateAuthException':
        errorMessage = i18n.t('shipment.errorMessages.ThreeDsCreateAuthException') as string;
        break;
      case 'CreditCardChallengeRequired':
        break;
      case 'IsCommercialInvoiceRequiredException':
        redirectUrl = '/shipment/customs';
        break;
      case 'FreeDomicileLimitException':
      case 'CarrierPermissionsValidateException':
      case 'EmptyEstimatedPriceException':
        redirectUrl = '/shipment/results';
        break;
      case 'IsPrintDisabledException':
      case 'IsCreditCardValidException':
        redirectUrl = '/shipment/checkout?edit=1';
        break;
      case 'IsVerificationRequiredException':
        redirectUrl = '/shipment/checkout/verify';
        break;
      case 'IsLoginDisabledException':
        redirectUrl = '/logout?redirectUrl=/shipment/create';
        break;
      case 'DangerousGoodsException':
        redirectUrl = '/shipment/create';
        isWarning = true;
        errorTitle = i18n.t('notifications.DangerousGoods.title') as string;
        errorBanner = 'DangerousGoods';
        break;
      case 'PerfumeException':
        redirectUrl = '/shipment/create';
        isWarning = true;
        errorTitle = i18n.t('notifications.DangerousGoods.title') as string;
        errorBanner = 'Perfume';
        break;
      case 'CarrierException':
        redirectUrl = '/shipment/create';
        isCarrierMessage = true;
        break;
      case 'TermsAndConditionsValidateException':
        redirectUrl = `/terms?redirectUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        break;
      case 'InvalidEmailException':
      case 'FreightAndParcelPackageException':
        redirectUrl = '/shipment/create';
        break;
      default:
        redirectUrl = '';
    }

    const messageKey = `shipment.errorMessages.${errorType}`;
    if (i18n.te(messageKey)) {
      errorMessage = i18n.t(messageKey) as string;
    }

    return {
      successful: false,
      errorMessage,
      redirectUrl,
      secureshipTrackingNo: '',
      carrierTrackingNos: [],
      errorType,
      isWarning,
      isCarrierMessage,
      errorTitle,
      errorBanner,
      isExternalLink,
      threeDsChallenge,
    };
  }
};

export const checkCardVerificationRequired = async (): Promise<boolean> => {
  try {
    const response: AxiosResponse = await axiosService.get('/shipment/verification');
    if (response.data) return response.data.verificationRequired;
    return false;
  } catch (err) {
    return false;
  }
};

export const getPaymentInformation = async (): Promise<PaymentInfoResponse | null> => {
  try {
    const response: AxiosResponse = await axiosService.get('/account/payment-information');
    return response.data;
  } catch (err) {
    return null;
  }
};

export const addPaymentInformation = async (
  cardInfo: PaymentInfoRequest,
): Promise<PaymentSavedResponse | null> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      '/account/payment-information',
      cardInfo,
    );
    return response.data;
  } catch (err) {
    const errResponse = parseError(err);
    return {
      success: false,
      error: errResponse.errorMessage,
      cardType: 'Unknown',
      expiry: cardInfo.month,
    };
  }
};

export const getShippingTips = async (countryCode: string): Promise<ShippingTips | null> => {
  try {
    const response: AxiosResponse = await axiosService.get(`/countries/shipping-tips/${countryCode}`);
    return response.data;
  } catch (err) {
    return null;
  }
};

export const getShipmentPrintData = async (
  secureshipTrackingNo: string,
): Promise<ShipmentPrintData | null> => {
  try {
    const response: AxiosResponse = await axiosService.get(`/shipment/print/${secureshipTrackingNo}`);
    if (!response.data) return null;
    return response.data;
  } catch (err) {
    return null;
  }
};

export const getShipmentPrintLabelPdf = async (
  secureshipTrackingNo: string,
  documentSize: keyof typeof PrintLabelSize = 'Thermal',
  getQrCode = false,
): Promise<unknown> => {
  if (!secureshipTrackingNo) return null;

  try {
    const token = await getTokenAsQuery(axiosServiceV2);
    if (!token) return null;
    const response: AxiosResponse = await axiosServiceV2.get(
      `/shipment/print/documents/${secureshipTrackingNo}?documentSize=${documentSize}&getQrCode=${getQrCode}&bearerToken=${encodeURIComponent(token)}`,
      { responseType: 'arraybuffer' },
    );
    if (!response.data) return null;
    return response.data;
  } catch (err) {
    return null;
  }
};

export const getShipmentPrintLabelSrc = async (
  secureshipTrackingNo: string,
  documentSize: keyof typeof PrintLabelSize = 'Thermal',
  getQrCode = false,
): Promise<string | null> => {
  if (!secureshipTrackingNo) return null;

  const token = await getTokenAsQuery(axiosServiceV2);
  const requestUrl = `/shipment/print/documents/${secureshipTrackingNo}?documentSize=${documentSize}&getQrCode=${getQrCode}&bearerToken=${encodeURIComponent(token)}`;

  try {
    await axiosServiceV2.get(requestUrl);
  } catch {
    return null;
  }

  return `${baseURLV2}${requestUrl}`;
};

export const getShipmentPrintLabelMultipleAsZip = async (
  trackingNumbers: string[],
  documentSize: keyof typeof PrintLabelSize = 'Thermal',
  getQrCode = false,
): Promise<{
  data?: string | null,
} & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosServiceV2.post(
      `/shipment/print/documents?documentSize=${documentSize}&getQrCode=${getQrCode}`, {
        trackingNumbers,
      }, { responseType: 'blob' },
    );

    return {
      successful: true,
      data: response.data,
      redirectUrl: '',
      errorMessage: '',
    };
  } catch (error) {
    return filterExceptionMessage(
      error,
      'shipment.errorMessages.DownloadPrintDocumentsLimitException',
    );
  }
};

export const getIntegrationsForImportData = async (
  platformId: number,
  orderId: string,
): Promise<IntegrationsResponse> => {
  try {
    const response: AxiosResponse = await axiosService.get(
      `/integrations/${platformId}/order/${encodeURIComponent(orderId)}`,
    );
    return response.data;
  } catch (err) {
    const errorResponse = filterExceptionMessage(
      err, 'shipment.errorMessages.ShopifyMissingAccessScopesException',
    );
    return {
      ...errorResponse,
      message: errorResponse.errorMessage,
    };
  }
};

export const checkToShowSummaryPage = async (): Promise<boolean> => {
  try {
    const response: AxiosResponse = await axiosService.get('/shipment/show-summary-page');
    if (response.data) {
      return response.data.showSummaryPage;
    }
    return false;
  } catch (err) {
    return false;
  }
};

export const confirmVerificationAmount = async (
  amount: number,
): Promise<AmountVerificationResponse | null> => {
  try {
    const response: AxiosResponse = await axiosService.get(`/shipment/verification/${amount}`);
    return response.data;
  } catch (err) {
    return null;
  }
};

export const fetchSupportedServiceLevels = async (): Promise<CarrierServiceLevel[]> => {
  try {
    const response: AxiosResponse = await axiosService.get('/carriers/supported-service-levels');
    return response.data;
  } catch (err) {
    return [];
  }
};

export const getCityProvinceResidential = async (
  countryCode: string,
  postalCode: string,
): Promise<CityProvinceResidentialResponse | null> => {
  try {
    const response: AxiosResponse = await axiosService.get(`/countries/city-province-lookup/${countryCode}/${postalCode}`);
    return response.data;
  } catch {
    return null;
  }
};

export const getCarriersClarificationAddresses = async (
  address: string,
): Promise<PredictionAddressDto[] | null> => {
  try {
    const res: AxiosResponse = await axiosService.get(`/maps/autocomplete/${address}`);

    return res.data;
  } catch {
    return null;
  }
};

export const getClarificationDetailFromMapAddress = async (
  placeId: string,
): Promise<ClarificationAddressDto[] | null> => {
  try {
    const res: AxiosResponse = await axiosService.get(`/maps/details/${placeId}`);

    return res.data;
  } catch {
    return null;
  }
};

export const perform3dsCardLookup = async (): Promise<ThreeDsCardLookupDto | null> => {
  const response: AxiosResponse = await axiosService.post('/account/3ds-card-lookup');
  return response.data;
};

export const getCountryMetadata = async (
  countryCode: string,
): Promise<CountryMetadataResponse | null> => {
  try {
    if (countryCode === 'GB') {
      const regionCodes = ['EN', 'SF', 'WL', 'NB'];

      const responses = await Promise.all(
        regionCodes.map((code) => axiosService.get(`/countries/metadata/${code}`).catch(() => null)),
      );

      const validResponses = responses
        .filter(Boolean)
        .map((res: any) => res.data);

      if (!validResponses.length) return null;

      return {
        alpha2: 'GB',
        name: 'United Kingdom',
        province: {
          required: true,
          showField: true,
          items: validResponses.flatMap((r) => r.province?.items || []),
        },
      };
    }

    const res: AxiosResponse = await axiosService.get(`/countries/metadata/${countryCode}`);
    return res.data;
  } catch {
    return null;
  }
};

export const getPayDetailsFromPlaceId = async (
  placeId: string,
): Promise<PayDetailsAddressDto[] | null> => {
  try {
    const res: AxiosResponse = await axiosService.get(`/maps/pay-details/${placeId}`);
    return res.data;
  } catch {
    return null;
  }
};
